import React, { useMemo } from 'react';
import { Button, Card, Flex, Select, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DELIVERY_OUTCOMES, DeliveryEvent, DeliveryEventFilters, DeliveryOutcome, isProblemOutcome } from '../../../domain/entities/DeliveryDashboard';
import DeliveryOutcomeTag from './DeliveryOutcomeTag.component';

interface DeliveryFeedProps {
  events: DeliveryEvent[];
  total: number;
  page: number;
  perPage: number;
  isLoading: boolean;
  filters: DeliveryEventFilters;
  errorCodes: string[];
  filterLabels: { account?: string; channel?: string };
  onFiltersChange: (filters: Partial<DeliveryEventFilters>) => void;
  onClearFilters: () => void;
  onPageChange: (page: number) => void;
  onOpen: (uuid: string) => void;
}

const shortLink = (link: string) => link.replace(/^https:\/\/t\.me\//, '');

const DeliveryFeed: React.FC<DeliveryFeedProps> = ({
  events, total, page, perPage, isLoading, filters, errorCodes, filterLabels, onFiltersChange, onClearFilters, onPageChange, onOpen,
}) => {
  const { t, i18n } = useTranslation();
  const dateFormat = useMemo(() => new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }), [i18n.language]);
  const now = Date.now();
  const errorLabel = (code: string) => t(`delivery-error-${code}`, { ns: 'labels', defaultValue: code });

  const hasFilters = filters.outcomes.length > 0 || Boolean(filters.telegramAccountUuid || filters.channelUuid || filters.errorCode || filters.day);

  const columns: ColumnsType<DeliveryEvent> = [
    {
      title: t('delivery-planned-at', { ns: 'labels' }),
      width: 150,
      render: (_, event) => (
        <Flex vertical>
          <Typography.Text>{dateFormat.format(new Date(event.plannedAt))}</Typography.Text>
          {new Date(event.plannedAt).getTime() > now && <Typography.Text type="secondary" className="delivery-feed-future">{t('delivery-future', { ns: 'labels' })}</Typography.Text>}
        </Flex>
      ),
    },
    { title: t('delivery-status', { ns: 'labels' }), width: 170, render: (_, event) => <DeliveryOutcomeTag outcome={event.outcome} /> },
    {
      title: t('delivery-advert', { ns: 'labels' }),
      render: (_, event) => event.advertTitle || <Typography.Text type="secondary">{t('delivery-deleted', { ns: 'labels' })}</Typography.Text>,
    },
    {
      title: t('channel-link', { ns: 'labels' }),
      render: (_, event) => (event.channelLink ? shortLink(event.channelLink) : <Typography.Text type="secondary">{t('delivery-deleted', { ns: 'labels' })}</Typography.Text>),
    },
    {
      title: t('delivery-account', { ns: 'labels' }),
      render: (_, event) => (
        <Flex vertical>
          <Typography.Text>{event.telegramAccountPhone || event.telegramAccountUuid}</Typography.Text>
          {event.clientEmail && <Typography.Text type="secondary" className="delivery-feed-client">{event.clientEmail}</Typography.Text>}
        </Flex>
      ),
    },
    {
      title: t('delivery-error', { ns: 'labels' }),
      render: (_, event) => (event.errorCode && isProblemOutcome(event.outcome)
        ? <Tag color="error">{errorLabel(event.errorCode)}</Tag>
        : null),
    },
  ];

  return (
    <Card size="small" title={t('delivery-feed-title', { ns: 'titles' })}>
      <Flex gap={8} wrap="wrap" align="center" className="delivery-feed-filters">
        <Select<DeliveryOutcome[]>
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          className="delivery-feed-filter"
          placeholder={t('delivery-status', { ns: 'labels' })}
          value={filters.outcomes}
          options={DELIVERY_OUTCOMES.map((outcome) => ({ value: outcome, label: t(`delivery-outcome-${outcome}`, { ns: 'labels' }) }))}
          onChange={(outcomes) => onFiltersChange({ outcomes })}
        />
        <Select<string>
          allowClear
          className="delivery-feed-filter"
          placeholder={t('delivery-error', { ns: 'labels' })}
          value={filters.errorCode}
          options={errorCodes.map((code) => ({ value: code, label: errorLabel(code) }))}
          onChange={(errorCode) => onFiltersChange({ errorCode })}
        />
        {filters.telegramAccountUuid && (
          <Tag closable onClose={() => onFiltersChange({ telegramAccountUuid: undefined })}>
            {t('delivery-account', { ns: 'labels' })}: {filterLabels.account ?? filters.telegramAccountUuid}
          </Tag>
        )}
        {filters.channelUuid && (
          <Tag closable onClose={() => onFiltersChange({ channelUuid: undefined })}>
            {t('channel-link', { ns: 'labels' })}: {filterLabels.channel ? shortLink(filterLabels.channel) : filters.channelUuid}
          </Tag>
        )}
        {filters.day && (
          <Tag closable onClose={() => onFiltersChange({ day: undefined })}>
            {t('delivery-day', { ns: 'labels' })}: {filters.day}
          </Tag>
        )}
        {hasFilters && <Button size="small" type="link" onClick={onClearFilters}>{t('reset-filters', { ns: 'buttons' })}</Button>}
      </Flex>

      <Table<DeliveryEvent>
        size="small"
        rowKey={(event) => event.uuid}
        columns={columns}
        dataSource={events}
        loading={isLoading}
        scroll={{ x: 960 }}
        rowClassName={(event) => (isProblemOutcome(event.outcome) ? 'delivery-feed-row-problem' : '')}
        onRow={(event) => ({ onClick: () => onOpen(event.uuid), className: 'delivery-feed-row' })}
        locale={{ emptyText: t('delivery-feed-empty', { ns: 'labels' }) }}
        pagination={{
          current: page,
          pageSize: perPage,
          total,
          showSizeChanger: false,
          showTotal: (count) => t('delivery-feed-total', { ns: 'labels', count }),
          onChange: onPageChange,
        }}
      />
    </Card>
  );
};

export default DeliveryFeed;
